import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { catchError, Subscription, tap } from 'rxjs';
import { AuthService } from '../auth.service';
import { FlowComponent } from '../controls/flow/flow.component';

import { LogoutComponent } from '../controls/logout/logout.component';
import { LoginFlow, UpdateLoginFlowBody } from '../../../sdk/kratos';
import { MiraLogoComponent } from '../../ui/mira-logo/mira-logo.component';
import { ThemeToggleComponent } from '../../ui/theme-toggle/theme-toggle.component';

@Component({
    selector: 'app-login',
    imports: [
    MiraLogoComponent,
    ThemeToggleComponent,
    FlowComponent,
    RouterLink,
    LogoutComponent
],
    templateUrl: './login.component.html',
    styleUrl: './login.component.less'
})
export class LoginComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  flow?: LoginFlow;
  // The aal is set as a query parameter by your Ory project
  // aal1 is the default authentication level (Single-Factor)
  // aal2 is a query parameter that can be used to request Two-Factor authentication
  // https://www.ory.sh/docs/kratos/mfa/overview
  aal?: string;
  refresh?: string;
  logoutToken?: string;

  registrationQueryParams: Params = {};
  recoveryQueryParams: Params = {};

  get title(): string {
    if (this.flow?.refresh) {
      return 'Confirm Action';
    } else if (this.flow?.requested_aal === 'aal2') {
      return 'Two-Factor Authentication';
    } else {
      return 'Sign In';
    }
  }

  constructor(private authService: AuthService,
              private activatedRoute: ActivatedRoute) {
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.activatedRoute.queryParamMap.subscribe((paramMap) => {
        const flowId = paramMap.get('flow') || undefined;
        const aal = paramMap.get('aal') || undefined;
        // The return_to is a query parameter is set by you when you would like to redirect
        // the user back to a specific URL after login is successful
        // In most cases it is not necessary to set a return_to if the UI business logic is
        // handled by the SPA.
        //
        // In OAuth flows this value might be ignored in favor of keeping the OAuth flow
        // intact between multiple flows (e.g. Login -> Recovery -> Settings -> OAuth2 Consent)
        // https://www.ory.sh/docs/oauth2-oidc/identity-provider-integration-settings
        const returnTo = paramMap.get('return_to') || undefined;
        // The login_challenge is a query parameter set by the Ory OAuth2 login flow
        // Switching between pages should keep the login_challenge in the URL so that the
        // OAuth flow can be completed upon completion of another flow (e.g. Registration).
        // https://www.ory.sh/docs/oauth2-oidc/custom-login-consent/flow
        const loginChallenge = paramMap.get('login_challenge') || undefined;
        // A flow is already loaded for the same flow id, nothing to do. This also prevents
        // reloading the flow after an in-place flow replacement (e.g. a validation error).
        if (this.flow && this.flow.id === flowId) {
          return;
        }
        if (returnTo) {
          this.registrationQueryParams['return_to'] = returnTo;
          this.recoveryQueryParams['return_to'] = returnTo;
        }
        if ((aal && aal !== this.aal)) {
          this.aal = aal;
        }
        this.loadFlow(flowId, aal, returnTo, loginChallenge);
      })
    );
  }

  private loadFlow(flowId?: string, aal?: string, returnTo?: string, loginChallenge?: string): void {
    this.subscription.add(
      this.authService.createOrGetLoginFlow(flowId, aal, returnTo, loginChallenge)
        .pipe(tap((data) => {
          this.flow = data;
        }), catchError(err => {
          console.log('login returned err: ', err);
          return this.authService.handleGetFlowError(err, 'login', () => {
            this.flow = undefined;
          });
        }))
        .subscribe({
          next: (data) => {
            if (this.flow?.oauth2_login_request?.challenge) {
              this.registrationQueryParams['login_challenge'] = this.flow.oauth2_login_challenge;
              if (this.flow.return_to) {
                this.registrationQueryParams['return_to'] = this.flow.return_to;
              }
            }
          },
          error: (err)=> {
            console.log('login error after handler: ', err);
          }
        })
    );
  }

  onSubmit(values: UpdateLoginFlowBody): void {
    // On submission, add the flow ID to the URL but do not navigate. This prevents the user loosing
    // his data when she/he reloads the page.
    // updateFlowIdWithoutRefresh(String(this.flow?.id));
    this.subscription.add(
      this.authService.updateLoginFlow(values, String(this.flow?.id), this.flow?.return_to)
        .pipe(catchError((err) => {
          return this.authService.handleGetFlowError(err, 'login', () => {
            this.flow = undefined;
          });
        }))
        .subscribe({
          next: () => {},
          error: (err: any) => {
            console.log('submit login got error: ' + err);
            // If the previous handler did not catch the error it's most likely a form validation error
            if (err.status === 400) {
              // Yup, it is!
              this.flow = err.error as LoginFlow;
            }
          }
        })
    );
  }
}
